/**
 * useAdrAgent — Hook per il workflow Agent Mode ADR/SID
 *
 * State Machine:
 *   IDLE → ADR_INIT → ADR_STEP_{field} → ADR_REVIEW → ADR_GENERATE → ADR_DONE
 *                                                     ↓
 *                                          ADR_ERROR_RECOVERABLE
 *
 * Gestisce:
 * - State machine con transizioni
 * - Chiamate tool calling al backend (/api/adr/*)
 * - Schema campi e opzioni
 * - Validazione client + server
 * - Salvataggio bozze
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import api from '../services/api'

// ============================================================
// TYPES
// ============================================================

export type AdrState =
  | 'IDLE'
  | 'ADR_INIT'
  | 'ADR_STEP'
  | 'ADR_RAPPORTI'
  | 'ADR_REVIEW'
  | 'ADR_GENERATE'
  | 'ADR_DONE'
  | 'ADR_ERROR_RECOVERABLE'

export interface AdrFieldSchema {
  field: string
  label_it: string
  label_en: string
  type: 'select' | 'text' | 'number' | 'date'
  required: boolean
  maxLength?: number
  condition?: string
  group: string
  options_endpoint?: string
}

export interface AdrOption {
  value: string
  label: string
}

export interface AdrFieldError {
  field: string
  error_code: string
  message: string
}

export interface AdrTestataValues {
  tipoInvio: string
  tipoComunicazione: string
  annoRiferimento: string
  meseRiferimento: string
  codiceFiscaleObbligato: string
  cognome: string
  nome: string
  sesso: string
  dataNascita: string
  comuneNascita: string
  provinciaNascita: string
  denominazione: string
  comuneSedeLegale: string
  provinciaSedeLegale: string
  protocolloAnnullamento: string
  cfSocietaIncorporata: string
  dataChiusuraGeneralizzata: string
}

export interface AdrAgentMessage {
  id: string
  type: 'agent' | 'user' | 'system' | 'error'
  content: string
  field?: string
  fieldSchema?: AdrFieldSchema
  options?: AdrOption[]
  timestamp: string
}

/** Anagrafica per rapporto */
export interface AdrAnagraficaValues {
  codiceFiscale: string
  codiceRuolo: string
  cognome: string
  nome: string
  sesso: string
  dataNascita: string
  comuneNascita: string
  provinciaNascita: string
  denominazione: string
  comuneSedeLegale: string
  provinciaSedeLegale: string
}

const EMPTY_ANAGRAFICA: AdrAnagraficaValues = {
  codiceFiscale: '', codiceRuolo: '01',
  cognome: '', nome: '', sesso: '',
  dataNascita: '', comuneNascita: '', provinciaNascita: '',
  denominazione: '', comuneSedeLegale: '', provinciaSedeLegale: '',
}

/** Rapporto nel workflow agent */
export interface AdrRapportoValues {
  codiceUnivocoRapporto: string
  tipoRapporto: string
  dataInizio: string
  dataFine: string
  cab: string
  anagrafiche: AdrAnagraficaValues[]
}

const EMPTY_RAPPORTO: AdrRapportoValues = {
  codiceUnivocoRapporto: '',
  tipoRapporto: '01',
  dataInizio: '',
  dataFine: '',
  cab: '',
  anagrafiche: [{ ...EMPTY_ANAGRAFICA }],
}

const EMPTY_TESTATA: AdrTestataValues = {
  tipoInvio: '', tipoComunicazione: '', annoRiferimento: '', meseRiferimento: '',
  codiceFiscaleObbligato: '', cognome: '', nome: '', sesso: '',
  dataNascita: '', comuneNascita: '', provinciaNascita: '',
  denominazione: '', comuneSedeLegale: '', provinciaSedeLegale: '',
  protocolloAnnullamento: '', cfSocietaIncorporata: '', dataChiusuraGeneralizzata: '',
}

// ============================================================
// HOOK
// ============================================================

export function useAdrAgent() {
  const [state, setState] = useState<AdrState>('IDLE')
  const [schema, setSchema] = useState<AdrFieldSchema[]>([])
  const [currentField, setCurrentField] = useState<string | null>(null)
  const [currentOptions, setCurrentOptions] = useState<AdrOption[]>([])
  const [values, setValues] = useState<AdrTestataValues>({ ...EMPTY_TESTATA })
  const [errors, setErrors] = useState<AdrFieldError[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [messages, setMessages] = useState<AdrAgentMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [personaType, setPersonaType] = useState<'pf' | 'pnf' | null>(null)
  const [draftId, setDraftId] = useState<string | null>(null)
  const [generatedContent, setGeneratedContent] = useState<string | null>(null)
  const [generatedFilename, setGeneratedFilename] = useState<string | null>(null)
  const [rapporti, setRapporti] = useState<AdrRapportoValues[]>([{ ...EMPTY_RAPPORTO, anagrafiche: [{ ...EMPTY_ANAGRAFICA }] }])
  const [rapportoTypeOptions, setRapportoTypeOptions] = useState<AdrOption[]>([])
  const [ruoloOptions, setRuoloOptions] = useState<AdrOption[]>([])
  const stepHistoryRef = useRef<string[]>([])

  // Refs to avoid stale closures in useCallback
  const valuesRef = useRef(values)
  const personaTypeRef = useRef(personaType)
  const rapportiRef = useRef(rapporti)
  useEffect(() => { valuesRef.current = values }, [values])
  useEffect(() => { personaTypeRef.current = personaType }, [personaType])
  useEffect(() => { rapportiRef.current = rapporti }, [rapporti])

  // Helper: add message
  const addMessage = useCallback((type: AdrAgentMessage['type'], content: string, extra?: Partial<AdrAgentMessage>) => {
    const msg: AdrAgentMessage = {
      id: crypto.randomUUID(),
      type,
      content,
      timestamp: new Date().toISOString(),
      ...extra,
    }
    setMessages(prev => [...prev, msg])
    return msg
  }, [])

  // Determine which fields to show based on values
  const getVisibleFields = useCallback((): AdrFieldSchema[] => {
    return schema.filter(f => {
      if (!f.condition) return true
      // Parse conditions like "tipoComunicazione=9" or "tipoComunicazione=7|tipoComunicazione=8"
      const parts = f.condition.split('|')
      return parts.some(part => {
        const [key, val] = part.split('=')
        return values[key as keyof AdrTestataValues] === val
      })
    })
  }, [schema, values])

  // Get required fields for current step
  const getRequiredFieldsForStep = useCallback((): AdrFieldSchema[] => {
    const visible = getVisibleFields()
    // Filter: show fields based on persona type
    return visible.filter(f => {
      if (personaType === 'pf' && f.group === 'persona_non_fisica') return false
      if (personaType === 'pnf' && f.group === 'persona_fisica') return false
      return true
    })
  }, [getVisibleFields, personaType])

  // ============================================================
  // TOOL CALLS
  // ============================================================

  /** adr_start — Inizia il workflow */
  const startWorkflow = useCallback(async () => {
    setIsLoading(true)
    setErrors([])
    setWarnings([])
    setGeneratedContent(null)
    setGeneratedFilename(null)
    setValues({ ...EMPTY_TESTATA })
    setPersonaType(null)
    setDraftId(null)
    stepHistoryRef.current = []
    setMessages([])

    try {
      const [startRes, schemaRes] = await Promise.all([
        api.post('/adr/start'),
        api.get('/adr/schema'),
      ])

      const schemaData = schemaRes.data.schema as AdrFieldSchema[]
      setSchema(schemaData)

      const firstField = startRes.data.session?.first_field || 'tipoInvio'
      setCurrentField(firstField)
      setState('ADR_STEP')

      // Fetch options if select field
      const fieldDef = schemaData.find(f => f.field === firstField)
      if (fieldDef?.type === 'select' && fieldDef.options_endpoint) {
        const optRes = await api.get(`/adr/options/${firstField}`)
        setCurrentOptions(optRes.data.options || [])
      } else {
        setCurrentOptions([])
      }

      addMessage('agent', 'Iniziamo la compilazione della comunicazione AdR. Seleziona il tipo di invio.', {
        field: firstField,
        fieldSchema: fieldDef,
        options: fieldDef?.type === 'select' ? currentOptions : undefined,
      })

      stepHistoryRef.current.push(firstField)
    } catch (err) {
      console.error('[AdrAgent] start error:', err)
      setState('ADR_ERROR_RECOVERABLE')
      addMessage('error', 'Errore nell\'avvio del workflow. Riprova.')
    } finally {
      setIsLoading(false)
    }
  }, [addMessage, currentOptions])

  /** adr_set_field — Imposta un campo e avanza */
  const setField = useCallback(async (field: string, value: string) => {
    setIsLoading(true)

    try {
      const res = await api.post('/adr/set-field', { field, value })
      const data = res.data

      if (!data.ok) {
        setErrors([{ field, error_code: data.error_code, message: data.message }])
        addMessage('error', data.message || 'Valore non valido')
        setIsLoading(false)
        return
      }

      // Update value
      setValues(prev => ({ ...prev, [field]: value }))
      setErrors(prev => prev.filter(e => e.field !== field))

      // Detect persona type
      if (data.persona_type) {
        setPersonaType(data.persona_type)
      }

      // Add user message
      const optLabel = currentOptions.find(o => o.value === value)?.label
      addMessage('user', optLabel || value)

      // Step-transition confirmation: show what was saved
      const fieldDef = schema.find(f => f.field === field)
      addMessage('system', `✓ ${fieldDef?.label_it || field}: ${optLabel || value}`)

      // Navigate to next field
      const allFields = getRequiredFieldsForStep()
      const currentIdx = allFields.findIndex(f => f.field === field)
      let nextField: string | null = null

      if (currentIdx >= 0 && currentIdx < allFields.length - 1) {
        nextField = allFields[currentIdx + 1].field
      }

      // If we have persona type info and haven't yet navigated PF/PNF fields
      if (field === 'codiceFiscaleObbligato' && data.persona_type) {
        // Jump to the appropriate group fields
        const pfFields = schema.filter(f => f.group === 'persona_fisica')
        const pnfFields = schema.filter(f => f.group === 'persona_non_fisica')
        nextField = data.persona_type === 'pf' ? pfFields[0]?.field : pnfFields[0]?.field

        // Inform user which persona path was detected
        const personaLabel = data.persona_type === 'pf' ? 'Persona Fisica' : 'Persona Non Fisica (P.IVA)'
        addMessage('agent', `Rilevato soggetto: ${personaLabel}. Compila i dati anagrafici.`)
      }

      if (nextField) {
        setCurrentField(nextField)
        stepHistoryRef.current.push(nextField)

        // Show progress hint
        const totalSteps = allFields.length
        const nextIdx = allFields.findIndex(f => f.field === nextField)
        const progressPct = nextIdx >= 0 ? Math.round(((nextIdx + 1) / totalSteps) * 100) : 0

        // Fetch options for next field
        const nextDef = schema.find(f => f.field === nextField)
        if (nextDef?.type === 'select' && nextDef.options_endpoint) {
          const optRes = await api.get(`/adr/options/${nextField}`)
          setCurrentOptions(optRes.data.options || [])
          addMessage('agent', `${progressPct}% — Seleziona: ${nextDef.label_it}`, { field: nextField, fieldSchema: nextDef, options: optRes.data.options })
        } else {
          setCurrentOptions([])
          const hint = nextDef?.maxLength ? ` (max ${nextDef.maxLength} car.)` : ''
          addMessage('agent', `${progressPct}% — Inserisci: ${nextDef?.label_it || nextField}${hint}`, { field: nextField, fieldSchema: nextDef || undefined })
        }
      } else {
        // No more required testata fields → go to rapporti step
        setCurrentField(null)
        setState('ADR_RAPPORTI')

        // Fetch rapporto type options and ruolo options
        try {
          const [rtRes, rrRes] = await Promise.all([
            api.get('/adr/options/tipoRapporto'),
            api.get('/adr/options/codiceRuolo'),
          ])
          setRapportoTypeOptions(rtRes.data.options || [])
          setRuoloOptions(rrRes.data.options || [])
        } catch { /* ignore, user can still type */ }

        // Build summary of testata for the message
        const updatedValues = { ...valuesRef.current, [field]: value }
        const filledSummary = schema
          .filter(f => {
            const pt = personaTypeRef.current
            if (pt === 'pf' && f.group === 'persona_non_fisica') return false
            if (pt === 'pnf' && f.group === 'persona_fisica') return false
            return !!updatedValues[f.field as keyof AdrTestataValues]
          })
          .map(f => `  • ${f.label_it}: ${updatedValues[f.field as keyof AdrTestataValues]}`)
          .join('\n')

        addMessage('agent', `Testata completata ✓\n${filledSummary}\n\nOra compila i rapporti finanziari, poi premi "Vai al Riepilogo".`)
      }
    } catch (err) {
      console.error('[AdrAgent] set-field error:', err)
      addMessage('error', 'Errore nella validazione del campo. Riprova.')
    } finally {
      setIsLoading(false)
    }
  }, [schema, currentOptions, getRequiredFieldsForStep, addMessage])

  /** Go back to previous field */
  const goBack = useCallback(() => {
    if (stepHistoryRef.current.length <= 1) return

    stepHistoryRef.current.pop()
    const prevField = stepHistoryRef.current[stepHistoryRef.current.length - 1]
    setCurrentField(prevField)
    setState('ADR_STEP')

    const fieldDef = schema.find(f => f.field === prevField)
    if (fieldDef?.type === 'select' && fieldDef.options_endpoint) {
      api.get(`/adr/options/${prevField}`)
        .then(res => setCurrentOptions(res.data.options || []))
        .catch(() => setCurrentOptions([]))
    } else {
      setCurrentOptions([])
    }

    addMessage('system', `Torna al campo: ${fieldDef?.label_it || prevField}`)
  }, [schema, addMessage])

  /** Go from rapporti to review */
  const goToReview = useCallback(() => {
    setState('ADR_REVIEW')
    // Build summary
    const raps = rapportiRef.current
    const rapSummary = raps.map((r, i) => {
      const typeLabel = rapportoTypeOptions.find(o => o.value === r.tipoRapporto)?.label || r.tipoRapporto
      return `  Rapporto #${i + 1}: ${typeLabel} — ${r.codiceUnivocoRapporto || '(senza codice)'} — ${r.anagrafiche.length} anagrafiche`
    }).join('\n')
    addMessage('agent', `Rapporti configurati:\n${rapSummary}\n\nControlla il riepilogo e premi "Valida e Genera".`)
  }, [rapportoTypeOptions, addMessage])

  /** Go back from rapporti to last testata field */
  const goBackFromRapporti = useCallback(() => {
    if (stepHistoryRef.current.length >= 1) {
      const prevField = stepHistoryRef.current[stepHistoryRef.current.length - 1]
      setCurrentField(prevField)
      setState('ADR_STEP')
      const fieldDef = schema.find(f => f.field === prevField)
      if (fieldDef?.type === 'select' && fieldDef.options_endpoint) {
        api.get(`/adr/options/${prevField}`)
          .then(res => setCurrentOptions(res.data.options || []))
          .catch(() => setCurrentOptions([]))
      } else {
        setCurrentOptions([])
      }
      addMessage('system', `Torna al campo: ${fieldDef?.label_it || prevField}`)
    }
  }, [schema, addMessage])

  /** Update a rapporto field */
  const updateRapporto = useCallback((rapIdx: number, field: keyof AdrRapportoValues, value: unknown) => {
    setRapporti(prev => prev.map((r, i) => i === rapIdx ? { ...r, [field]: value } : r))
  }, [])

  /** Add a new rapporto */
  const addRapporto = useCallback(() => {
    setRapporti(prev => [...prev, { ...EMPTY_RAPPORTO, anagrafiche: [{ ...EMPTY_ANAGRAFICA }] }])
    addMessage('system', `Rapporto #${rapporti.length + 1} aggiunto.`)
  }, [rapporti.length, addMessage])

  /** Remove a rapporto */
  const removeRapporto = useCallback((idx: number) => {
    if (rapporti.length <= 1) return
    setRapporti(prev => prev.filter((_, i) => i !== idx))
    addMessage('system', `Rapporto #${idx + 1} rimosso.`)
  }, [rapporti.length, addMessage])

  /** Update anagrafica inside a rapporto */
  const updateAnagrafica = useCallback((rapIdx: number, anagIdx: number, field: keyof AdrAnagraficaValues, value: string) => {
    setRapporti(prev => prev.map((r, i) => i === rapIdx ? {
      ...r,
      anagrafiche: r.anagrafiche.map((a, j) => j === anagIdx ? { ...a, [field]: value } : a),
    } : r))
  }, [])

  /** Add anagrafica to a rapporto */
  const addAnagrafica = useCallback((rapIdx: number) => {
    setRapporti(prev => prev.map((r, i) => i === rapIdx ? {
      ...r,
      anagrafiche: [...r.anagrafiche, { ...EMPTY_ANAGRAFICA }],
    } : r))
  }, [])

  /** Remove anagrafica from a rapporto */
  const removeAnagrafica = useCallback((rapIdx: number, anagIdx: number) => {
    setRapporti(prev => prev.map((r, i) => i === rapIdx ? {
      ...r,
      anagrafiche: r.anagrafiche.filter((_, j) => j !== anagIdx),
    } : r))
  }, [])

  /** adr_validate — Validazione completa */
  const validate = useCallback(async () => {
    setIsLoading(true)

    try {
      const comunicazione = buildComunicazione()
      const res = await api.post('/adr/validate', comunicazione)
      const data = res.data

      if (data.ok || data.valid) {
        setErrors([])
        setWarnings(data.warnings || [])
        addMessage('agent', 'Validazione completata con successo! Puoi procedere alla generazione del file.')
        return true
      } else {
        const errs = (data.errors || []).map((e: { message: string; error_code?: string }) => ({
          field: 'validation',
          error_code: e.error_code || 'VALIDATION_ERROR',
          message: e.message,
        }))
        setErrors(errs)
        setWarnings(data.warnings || [])
        setState('ADR_ERROR_RECOVERABLE')
        addMessage('error', `Validazione fallita:\n${errs.map((e: AdrFieldError) => `• ${e.message}`).join('\n')}`)
        return false
      }
    } catch (err) {
      console.error('[AdrAgent] validate error:', err)
      addMessage('error', 'Errore durante la validazione.')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [addMessage])

  /** adr_generate — Genera file TXT */
  const generate = useCallback(async () => {
    setIsLoading(true)
    setState('ADR_GENERATE')

    try {
      const comunicazione = buildComunicazione()
      const res = await api.post('/adr/generate', comunicazione)
      const data = res.data

      if (data.ok) {
        setGeneratedContent(data.content)
        setGeneratedFilename(data.filename)
        setState('ADR_DONE')
        addMessage('agent', `File generato con successo: ${data.filename}\n\nStatistiche: ${JSON.stringify(data.stats)}`)
        return data
      } else {
        const errs = (data.errors || []).map((e: { message: string; error_code?: string }) => ({
          field: 'generation',
          error_code: e.error_code || 'GENERATION_ERROR',
          message: e.message,
        }))
        setErrors(errs)
        setState('ADR_ERROR_RECOVERABLE')
        addMessage('error', `Generazione fallita:\n${errs.map((e: AdrFieldError) => `• ${e.message}`).join('\n')}`)
        return null
      }
    } catch (err) {
      console.error('[AdrAgent] generate error:', err)
      setState('ADR_ERROR_RECOVERABLE')
      addMessage('error', 'Errore durante la generazione del file.')
      return null
    } finally {
      setIsLoading(false)
    }
  }, [addMessage])

  /** adr_save_draft — Salva bozza */
  const saveDraft = useCallback(async () => {
    setIsLoading(true)

    try {
      const comunicazione = buildComunicazione()
      const res = await api.post('/adr/save-draft', { comunicazione, id: draftId })
      const data = res.data

      if (data.ok) {
        setDraftId(data.id)
        addMessage('system', `Bozza salvata (ID: ${data.id})`)
        return data.id
      } else {
        addMessage('error', 'Errore nel salvataggio della bozza.')
        return null
      }
    } catch (err) {
      console.error('[AdrAgent] save-draft error:', err)
      addMessage('error', 'Errore nel salvataggio della bozza.')
      return null
    } finally {
      setIsLoading(false)
    }
  }, [draftId, addMessage])

  /** adr_load_draft — Carica bozza */
  const loadDraft = useCallback(async (id: string) => {
    setIsLoading(true)

    try {
      const res = await api.get(`/adr/load-draft/${id}`)
      const data = res.data

      if (data.ok && data.comunicazione) {
        const t = data.comunicazione.testata
        setValues({
          tipoInvio: t.tipoInvio || '',
          tipoComunicazione: t.tipoComunicazione || '',
          annoRiferimento: t.annoRiferimento || '',
          meseRiferimento: t.meseRiferimento || '',
          codiceFiscaleObbligato: t.codiceFiscaleObbligato || '',
          cognome: t.cognome || '',
          nome: t.nome || '',
          sesso: t.sesso || '',
          dataNascita: t.dataNascita || '',
          comuneNascita: t.comuneNascita || '',
          provinciaNascita: t.provinciaNascita || '',
          denominazione: t.denominazione || '',
          comuneSedeLegale: t.comuneSedeLegale || '',
          provinciaSedeLegale: t.provinciaSedeLegale || '',
          protocolloAnnullamento: t.protocolloAnnullamento || '',
          cfSocietaIncorporata: t.cfSocietaIncorporata || '',
          dataChiusuraGeneralizzata: t.dataChiusuraGeneralizzata || '',
        })
        setDraftId(id)
        setState('ADR_REVIEW')
        // Detect persona type from CF
        const cf = t.codiceFiscaleObbligato?.trim()
        if (cf && cf.length === 16) setPersonaType('pf')
        else if (cf && cf.length === 11) setPersonaType('pnf')
        addMessage('system', `Bozza caricata: ${id}`)
      } else {
        addMessage('error', 'Bozza non trovata.')
      }
    } catch (err) {
      console.error('[AdrAgent] load-draft error:', err)
      addMessage('error', 'Errore nel caricamento della bozza.')
    } finally {
      setIsLoading(false)
    }
  }, [addMessage])

  /** Reset to IDLE */
  const reset = useCallback(() => {
    setState('IDLE')
    setCurrentField(null)
    setCurrentOptions([])
    setValues({ ...EMPTY_TESTATA })
    setErrors([])
    setWarnings([])
    setMessages([])
    setPersonaType(null)
    setDraftId(null)
    setGeneratedContent(null)
    setGeneratedFilename(null)
    setRapporti([{ ...EMPTY_RAPPORTO, anagrafiche: [{ ...EMPTY_ANAGRAFICA }] }])
    setRapportoTypeOptions([])
    setRuoloOptions([])
    stepHistoryRef.current = []
  }, [])

  /** Go back to review from error state */
  const retryFromError = useCallback(() => {
    setState('ADR_REVIEW')
    setErrors([])
  }, [])

  // ============================================================
  // BUILD COMUNICAZIONE (uses refs to avoid stale closures)
  // ============================================================

  function buildComunicazione(): { testata: Record<string, unknown>; gruppiRapporto?: Record<string, unknown>[] } {
    const v = valuesRef.current
    const pt = personaTypeRef.current
    const raps = rapportiRef.current
    const t: Record<string, unknown> = {
      tipoInvio: v.tipoInvio,
      tipoComunicazione: v.tipoComunicazione,
      annoRiferimento: v.annoRiferimento,
      meseRiferimento: v.meseRiferimento,
      codiceFiscaleObbligato: v.codiceFiscaleObbligato.trim().toUpperCase(),
    }

    if (pt === 'pf') {
      t.cognome = v.cognome
      t.nome = v.nome
      t.sesso = v.sesso
      t.dataNascita = v.dataNascita
      t.comuneNascita = v.comuneNascita
      t.provinciaNascita = v.provinciaNascita
    } else {
      t.denominazione = v.denominazione
      t.comuneSedeLegale = v.comuneSedeLegale
      t.provinciaSedeLegale = v.provinciaSedeLegale
    }

    if (v.tipoComunicazione === '9') t.protocolloAnnullamento = v.protocolloAnnullamento
    if (['7', '8'].includes(v.tipoComunicazione)) t.cfSocietaIncorporata = v.cfSocietaIncorporata
    if (v.tipoComunicazione === 'C') {
      t.dataChiusuraGeneralizzata = v.dataChiusuraGeneralizzata
      t.flagCessazione = '1'
    }

    // Build gruppiRapporto
    const gruppiRapporto = raps
      .filter(r => r.codiceUnivocoRapporto || r.tipoRapporto)
      .map(r => ({
        rapporto: {
          codiceUnivocoRapporto: r.codiceUnivocoRapporto,
          tipoRapporto: r.tipoRapporto,
          dataInizio: r.dataInizio,
          dataFine: r.dataFine || undefined,
          cab: r.cab || undefined,
        },
        anagrafiche: r.anagrafiche.map((a, idx) => ({
          codiceUnivocoRapporto: r.codiceUnivocoRapporto,
          progressivo: idx + 1,
          dataInizioPartecipazione: a.dataNascita || r.dataInizio, // fallback
          flagAssenzaCF: a.codiceFiscale ? '0' : '1',
          codiceFiscale: a.codiceFiscale || undefined,
          codiceRuolo: a.codiceRuolo || '01',
          cognome: a.cognome || undefined,
          nome: a.nome || undefined,
          sesso: a.sesso || undefined,
          dataNascita: a.dataNascita || undefined,
          comuneNascita: a.comuneNascita || undefined,
          provinciaNascita: a.provinciaNascita || undefined,
          denominazione: a.denominazione || undefined,
          comuneSedeLegale: a.comuneSedeLegale || undefined,
          provinciaSedeLegale: a.provinciaSedeLegale || undefined,
        })),
      }))

    return { testata: t, ...(gruppiRapporto.length > 0 ? { gruppiRapporto } : {}) }
  }

  return {
    // State
    state,
    currentField,
    currentOptions,
    values,
    errors,
    warnings,
    messages,
    isLoading,
    personaType,
    draftId,
    generatedContent,
    generatedFilename,
    schema,
    rapporti,
    rapportoTypeOptions,
    ruoloOptions,

    // Computed
    getVisibleFields,
    getRequiredFieldsForStep,

    // Actions
    startWorkflow,
    setField,
    goBack,
    goToReview,
    goBackFromRapporti,
    validate,
    generate,
    saveDraft,
    loadDraft,
    reset,
    retryFromError,
    setValues,
    setPersonaType,
    addMessage,
    // Rapporti
    updateRapporto,
    addRapporto,
    removeRapporto,
    updateAnagrafica,
    addAnagrafica,
    removeAnagrafica,
    setRapporti,
  }
}
